/**
 * 다중 문서 스프레드시트 모델 — 자재 / IP·네트워크 공용.
 *
 * 한 현장(프로젝트)은 여러 개의 "문서"를 가진다. 각 문서는 엑셀 한 장에 대응하며
 * 자유 컬럼·행을 가진 schema-flexible 그리드다. 업로드한 엑셀 한 개 = 문서 한 개로,
 * 제목(파일명 기본, 수정 가능)으로 접었다 펼치는 토글 UI 로 보여준다.
 *
 * 저장은 백엔드 JSON 컬럼(projects.network_spreadsheet / projects.material_spreadsheet)에
 * 통째로 한다. 백엔드는 스키마를 검증하지 않으므로(opaque blob) 프론트가 모양을 책임진다.
 *
 * 하위호환: 과거 IP/네트워크는 단일 그리드 `{ columns, rows }`(version 1)로 저장했다.
 * `parseSpreadsheetDocuments` 가 이를 문서 1개로 감싸 자동 이관한다.
 */

/** 그리드 컬럼. id 는 행 cell 의 key (사용자에게 안 보임), name 은 헤더 라벨. */
export interface SpreadsheetColumn {
  id: string;
  name: string;
  /** 컬럼 폭(px). 기본 160. 사용자가 드래그로 조절. */
  width?: number;
}

/** 한 행. cells 의 key 는 SpreadsheetColumn.id. */
export interface SpreadsheetRow {
  id: string;
  cells: Record<string, string>;
}

/** 문서 하나 = 엑셀 한 장에 대응하는 명명된 그리드. */
export interface SpreadsheetDocument {
  id: string;
  /** 토글 제목. 업로드 시 파일명으로 생성되며 사용자가 수정 가능. */
  name: string;
  columns: SpreadsheetColumn[];
  rows: SpreadsheetRow[];
}

/** 현장별 저장 단위 — 문서 묶음. */
export interface SpreadsheetDocumentsData {
  documents: SpreadsheetDocument[];
  /** 스키마 버전. 2 = 다중 문서(현재). 1/누락 = 레거시 단일 그리드. */
  version: number;
}

/** 다중 문서 스키마 버전. */
export const SPREADSHEET_DOCS_VERSION = 2;

/** 엑셀 파싱 결과(excelImport.SheetGrid)와 구조적으로 동일한 입력 타입. */
export interface SpreadsheetGridInput {
  headers: string[];
  dataRows: string[][];
}

/** crypto.randomUUID 가 없는 구형 환경 fallback 포함 ID 생성기. */
export function newSpreadsheetId(prefix: string): string {
  const rand =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}-${rand}`;
}

/** 빈 문서 생성 — 기본 컬럼셋을 복제해 새 컬럼 id 를 부여한다. */
export function createEmptyDocument(
  name: string,
  defaultColumns: ReadonlyArray<Pick<SpreadsheetColumn, 'name' | 'width'>> = [],
): SpreadsheetDocument {
  const columns: SpreadsheetColumn[] =
    defaultColumns.length > 0
      ? defaultColumns.map((c) => ({ id: newSpreadsheetId('col'), name: c.name, width: c.width }))
      : [{ id: newSpreadsheetId('col'), name: '컬럼 1', width: 160 }];
  return { id: newSpreadsheetId('doc'), name, columns, rows: [] };
}

/** 파싱된 엑셀 그리드(헤더+행)로부터 새 문서를 만든다. 업로드 → 새 문서 경로. */
export function documentFromGrid(name: string, grid: SpreadsheetGridInput): SpreadsheetDocument {
  const columns: SpreadsheetColumn[] = grid.headers.map((h) => ({
    id: newSpreadsheetId('col'),
    name: h.trim() || '컬럼',
    width: 160,
  }));
  const rows: SpreadsheetRow[] = grid.dataRows.map((row) => {
    const cells: Record<string, string> = {};
    for (let i = 0; i < columns.length; i++) {
      const val = row[i] ?? '';
      if (val !== '') cells[columns[i].id] = val;
    }
    return { id: newSpreadsheetId('row'), cells };
  });
  return { id: newSpreadsheetId('doc'), name, columns, rows };
}

/**
 * 파싱된 엑셀 그리드를 기존 문서에 append.
 *  - 헤더 이름이 기존 컬럼 이름과 일치(trim)하면 그 컬럼 재사용.
 *  - 없으면 새 컬럼을 뒤에 추가(기존 행은 빈 셀).
 *  - 새 행은 기존 행 뒤에 append.
 * 반환은 새 문서 객체(불변 갱신).
 */
export function appendGridToDocument(
  doc: SpreadsheetDocument,
  grid: SpreadsheetGridInput,
): { document: SpreadsheetDocument; addedRows: number; addedColumns: number } {
  const nextColumns = [...doc.columns];
  const nameToId = new Map<string, string>();
  for (const c of nextColumns) nameToId.set(c.name.trim(), c.id);

  const headerColIds: string[] = [];
  let addedColumns = 0;
  for (const headerName of grid.headers) {
    const trimmed = headerName.trim();
    const existing = nameToId.get(trimmed);
    if (existing) {
      headerColIds.push(existing);
      continue;
    }
    const newCol: SpreadsheetColumn = { id: newSpreadsheetId('col'), name: trimmed || '컬럼', width: 160 };
    nextColumns.push(newCol);
    nameToId.set(trimmed, newCol.id);
    headerColIds.push(newCol.id);
    addedColumns += 1;
  }

  const newRows: SpreadsheetRow[] = grid.dataRows.map((row) => {
    const cells: Record<string, string> = {};
    for (let i = 0; i < headerColIds.length; i++) {
      const val = row[i] ?? '';
      if (val !== '') cells[headerColIds[i]] = val;
    }
    return { id: newSpreadsheetId('row'), cells };
  });

  return {
    document: { ...doc, columns: nextColumns, rows: [...doc.rows, ...newRows] },
    addedRows: newRows.length,
    addedColumns,
  };
}

/** unknown 값이 SpreadsheetColumn 배열 모양인지 확인. */
function isColumnArray(v: unknown): v is SpreadsheetColumn[] {
  return (
    Array.isArray(v) &&
    v.every((c) => c && typeof c === 'object' && typeof (c as { id?: unknown }).id === 'string')
  );
}

/** 한 문서 raw 를 정규화 — id/name/columns/rows 보강. */
function normalizeDocument(raw: unknown, fallbackName: string): SpreadsheetDocument | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  if (!isColumnArray(obj.columns)) return null;
  const rows = Array.isArray(obj.rows)
    ? (obj.rows as unknown[])
        .map((r) => {
          if (!r || typeof r !== 'object') return null;
          const rr = r as Record<string, unknown>;
          const cells =
            rr.cells && typeof rr.cells === 'object'
              ? (rr.cells as Record<string, string>)
              : {};
          return { id: typeof rr.id === 'string' ? rr.id : newSpreadsheetId('row'), cells };
        })
        .filter((r): r is SpreadsheetRow => r !== null)
    : [];
  return {
    id: typeof obj.id === 'string' ? obj.id : newSpreadsheetId('doc'),
    name: typeof obj.name === 'string' && obj.name.trim() ? obj.name : fallbackName,
    columns: obj.columns,
    rows,
  };
}

export interface ParseDocumentsOptions {
  /** 레거시 단일 그리드를 감쌀 때 / 빈 데이터 seed 시 문서 이름. */
  defaultDocName: string;
  /**
   * 저장된 데이터가 전혀 없을 때 seed 할 기본 컬럼셋.
   * 지정 시 빈 문서 1개를 만들어 첫 화면이 비지 않도록 한다. 비우면 문서 0개.
   */
  defaultColumns?: ReadonlyArray<Pick<SpreadsheetColumn, 'name' | 'width'>>;
}

/**
 * 백엔드 JSON(raw) 을 SpreadsheetDocumentsData 로 정규화.
 *  - null/빈값 → defaultColumns 가 있으면 기본 문서 1개, 없으면 문서 0개.
 *  - 레거시 `{ columns, rows }` → 문서 1개로 이관.
 *  - `{ documents: [...] }` → 각 문서 정규화.
 */
export function parseSpreadsheetDocuments(
  raw: unknown,
  opts: ParseDocumentsOptions,
): SpreadsheetDocumentsData {
  const seedEmpty = (): SpreadsheetDocumentsData => ({
    documents:
      opts.defaultColumns && opts.defaultColumns.length > 0
        ? [createEmptyDocument(opts.defaultDocName, opts.defaultColumns)]
        : [],
    version: SPREADSHEET_DOCS_VERSION,
  });

  if (raw === null || raw === undefined) return seedEmpty();

  let value: unknown = raw;
  if (typeof raw === 'string') {
    if (raw.trim() === '') return seedEmpty();
    try {
      value = JSON.parse(raw);
    } catch {
      return seedEmpty();
    }
  }
  if (!value || typeof value !== 'object') return seedEmpty();

  const obj = value as Record<string, unknown>;

  // 신규 다중 문서 모양.
  if (Array.isArray(obj.documents)) {
    const documents = (obj.documents as unknown[])
      .map((d, i) => normalizeDocument(d, `${opts.defaultDocName} ${i + 1}`))
      .filter((d): d is SpreadsheetDocument => d !== null);
    if (documents.length === 0) return seedEmpty();
    return { documents, version: SPREADSHEET_DOCS_VERSION };
  }

  // 레거시 단일 그리드 `{ columns, rows }`.
  if (isColumnArray(obj.columns)) {
    const doc = normalizeDocument(obj, opts.defaultDocName);
    if (doc) return { documents: [doc], version: SPREADSHEET_DOCS_VERSION };
  }

  return seedEmpty();
}
